import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {AuthService} from '../services/auth.service';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {filter, switchMap, take} from 'rxjs/operators';
import {AUTH_ACCESS_TOKEN} from '../models/auth.model';
import {LocalStorageService} from '../services/local-storage.service';
import {Store} from '@ngrx/store';
import {AppState} from '../store/core.state';
import {authLogout} from '../store/auth/auth.actions';

/*
* Ideas tomadas de https://stackoverflow.com/a/57638101/3384529
*/
@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private refreshTokenInProgress = false;
  private refreshTokenSubject: Subject<any> = new BehaviorSubject<any>(null);

  constructor(private authService: AuthService, private localStorageService: LocalStorageService, private store: Store<AppState>) {
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.needToken(request)) {
      return next.handle(request);
    }

    const accessExpired = this.authService.isAccessTokenExpired();

    if (accessExpired) {
      if (!this.refreshTokenInProgress) {
        this.refreshTokenInProgress = true;
        this.refreshTokenSubject.next(null);
        return this.authService.requestAccessToken().pipe(
          switchMap((tokens) => {
            if (tokens.access_token === '') {
              this.store.dispatch(authLogout());
              const status = 401;
              throw new HttpErrorResponse({
                error: 'Refresh token expiro.',
                status,
                statusText: 'Refresh token expiro.',
                url: request.url
              });
            }
            this.authService.saveTokens(tokens);
            this.refreshTokenInProgress = false;
            this.refreshTokenSubject.next(tokens.refresh_token);
            return next.handle(this.injectToken(request));
          }),
        );
      } else {
        return this.refreshTokenSubject.pipe(
          filter(result => result !== null),
          take(1),
          switchMap((res) => {
            return next.handle(this.injectToken(request));
          })
        );
      }
    }
    return next.handle(this.injectToken(request));
  }

  injectToken(request: HttpRequest<any>) {
    const accessToken = this.localStorageService.getItem(AUTH_ACCESS_TOKEN);
    return request.clone({
      body: {
        ...(request.body as any),
        token: accessToken
      }
    });
  }

  needToken(request: HttpRequest<any>) {
    let needToken = true;

    if (request.headers.get('no-authenticate')) {
      needToken = false;
    }

    return needToken;
  }
}
